import { ColumnDef } from "@tanstack/react-table"
import React, { useCallback, useEffect, useMemo, useState } from "react"
import DatePicker from "react-datepicker"
import { useTranslation } from "react-i18next"
import { useDispatch } from "react-redux"

import { handleToast } from "utils/messages"
import { history } from "utils/routes"

import useStateGetter from "hooks/useStateGetter"

import { getPlants } from "../../../companies/service/api"
import { Company } from "../../../companies/types"
import { compositionGetByCompanyId } from "../../../compositions/service/api"
import { Composition } from "../../../compositions/types"
import { fetchCountries } from "../../../countries/actions"
import { Country } from "../../../countries/types"
import { filterCompanyCompositions } from "../../../genericCompositions/utils"
import { MaterialGroup } from "../../../materials/service/api"
import {
  deleteSubmission,
  postConverterSubmissionInput,
  putSubmission,
} from "../../service/api"
import { SubmissionType } from "../../types"
import SubmissionGrid, { GridRow } from "./SubmissionGrid"
import { useGridSubmit } from "./useGridSubmit"

type ConvertingInputRow = {
  id?: number
  companyId?: number
  materialsGroupId?: number
  collectionDate?: Date
  countryId?: number
  compositionId?: number
  volumeNet?: number
  administrationNumber?: string
}

type LocalRow = GridRow<ConvertingInputRow>

const makeKey = () =>
  `r_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`

const ConvertingInputGrid: React.FC = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const [plants, setPlants] = useState<Company[]>([])
  const [compositionsByCompany, setCompositionsByCompany] = useState<
    Record<number, Composition[]>
  >({})
  const countriesEu = useStateGetter<Country[]>(["country", "countries"]) ?? []
  const countries = useMemo<Country[]>(
    () => [
      ...countriesEu,
      { id: 999, name: "Other", code: "NONEU" } as Country,
    ],
    [countriesEu],
  )

  const [rows, setRows] = useState<LocalRow[]>([{ key: makeKey(), row: {} }])

  const polymersOf = useCallback(
    (companyId?: number): MaterialGroup[] => {
      if (!companyId) return []
      const plant = plants.find((p) => p.id === companyId)
      return (plant?.polymers ?? []) as MaterialGroup[]
    },
    [plants],
  )

  const buildPayload = useCallback(
    (row: ConvertingInputRow) => {
      const country = countries.find((c) => c.id === row.countryId)
      const isMaterialFromOutsideEurope = country ? !country.isEu : false
      const volumeNet = row.volumeNet ?? 0
      return {
        companyId: row.companyId!,
        materialsGroupId: row.materialsGroupId!,
        countryId: row.countryId ?? null,
        compositionId: row.compositionId ?? null,
        collectionDate: row.collectionDate!,
        isInput: true,
        type: SubmissionType.CONVERTING,
        volumeGross: volumeNet,
        volumeNet,
        volumeNetOutside: 0,
        isMaterialFromOutsideEurope,
        canMaterialDemonstrateForAudit: false,
        totalDemonstratedDuringAudit: 0,
        administrationNumber: row.administrationNumber ?? "",
      }
    },
    [countries],
  )

  const { states, isSubmitting, submitAll, clearRowState } =
    useGridSubmit<ConvertingInputRow>({
      validate: (row) => {
        if (
          row.companyId == null ||
          row.materialsGroupId == null ||
          row.collectionDate == null ||
          row.countryId == null ||
          row.volumeNet == null
        ) {
          return null
        }
        const errors: string[] = []
        if (row.volumeNet <= 0) errors.push(t("Volume must be > 0"))
        return errors
      },
      create: async (row) => {
        const { data, errors } = await postConverterSubmissionInput(
          buildPayload(row) as any,
        )
        if (errors) return { errors: errors.map((e) => e.message) }
        return { id: data!.id! }
      },
      update: async (row) => {
        const { data, errors } = await putSubmission({
          ...buildPayload(row),
          id: row.id!,
        } as any)
        if (errors) return { errors: errors.map((e) => e.message) }
        return { id: data?.id ?? row.id! }
      },
    })

  useEffect(() => {
    dispatch(fetchCountries())
    getPlants().then((d) => {
      if (d.data) setPlants(d.data as Company[])
    })
  }, [dispatch])

  const ensureCompositions = useCallback((companyId: number) => {
    setCompositionsByCompany((prev) => {
      if (prev[companyId]) return prev
      compositionGetByCompanyId({ companyId }).then(({ data }) => {
        if (data) {
          setCompositionsByCompany((p) => ({
            ...p,
            [companyId]: data as Composition[],
          }))
        }
      })
      return prev
    })
  }, [])

  const updateRow = useCallback(
    (key: string, patch: Partial<ConvertingInputRow>) => {
      setRows((prev) =>
        prev.map((r) =>
          r.key === key ? { ...r, row: { ...r.row, ...patch } } : r,
        ),
      )
    },
    [],
  )

  const handleAdd = useCallback(() => {
    setRows((prev) => [...prev, { key: makeKey(), row: {} }])
  }, [])

  const handleRemove = useCallback(
    async (key: string) => {
      const target = rows.find((r) => r.key === key)
      clearRowState(key)
      if (target?.row.id != null) {
        const { errors } = await deleteSubmission({ id: target.row.id })
        if (errors) {
          handleToast(errors, "error")
          return
        }
      }
      setRows((prev) => prev.filter((r) => r.key !== key))
    },
    [clearRowState, rows],
  )

  const handleSubmit = useCallback(async () => {
    const { ok, savedCount, updated } = await submitAll(
      rows.map(({ key, row }) => ({ key, row })),
    )
    if (updated.length > 0) {
      setRows((prev) =>
        prev.map((r) => {
          const u = updated.find((x) => x.key === r.key)
          return u ? { ...r, row: { ...r.row, id: u.serverId } } : r
        }),
      )
    }
    if (ok && savedCount === 0) {
      handleToast(
        [t("Nothing to save — fill in the required fields first.") as string],
        "error",
      )
      return
    }
    if (ok) {
      handleToast([t("Submissions saved") as string])
      history.push("/submissions", { tab: "convertingInput" })
    } else {
      handleToast(
        [
          t("Some rows could not be saved. Check the highlighted rows.") as string,
        ],
        "error",
      )
    }
  }, [rows, submitAll, t])

  const columns = useMemo<ColumnDef<LocalRow, any>[]>(
    () => [
      {
        id: "company",
        header: () => <span>{t("Plant name")}</span>,
        cell: ({ row }) => {
          const r = row.original.row
          return (
            <select
              value={r.companyId ?? ""}
              onChange={(e) => {
                const companyId = e.target.value
                  ? Number(e.target.value)
                  : undefined
                if (companyId) ensureCompositions(companyId)
                updateRow(row.original.key, {
                  companyId,
                  materialsGroupId: undefined,
                  compositionId: undefined,
                })
              }}
            >
              <option value="">{t("Select…")}</option>
              {plants.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          )
        },
        size: 180,
      },
      {
        id: "polymer",
        header: () => <span>{t("Polymer")}</span>,
        cell: ({ row }) => {
          const r = row.original.row
          const list = polymersOf(r.companyId)
          return (
            <select
              value={r.materialsGroupId ?? ""}
              disabled={!r.companyId}
              onChange={(e) =>
                updateRow(row.original.key, {
                  materialsGroupId: e.target.value
                    ? Number(e.target.value)
                    : undefined,
                })
              }
            >
              <option value="">{t("Select…")}</option>
              {list.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          )
        },
        size: 140,
      },
      {
        id: "wasteOrigin",
        header: () => <span>{t("Waste origin")}</span>,
        cell: ({ row }) => {
          const r = row.original.row
          if (r.companyId && !compositionsByCompany[r.companyId]) {
            ensureCompositions(r.companyId)
          }
          const all = r.companyId ? compositionsByCompany[r.companyId] ?? [] : []
          const list = filterCompanyCompositions<Composition>(
            all,
            undefined,
            true,
          )
          return (
            <select
              value={r.compositionId ?? ""}
              disabled={!r.companyId}
              onChange={(e) =>
                updateRow(row.original.key, {
                  compositionId: e.target.value
                    ? Number(e.target.value)
                    : undefined,
                })
              }
            >
              <option value="">{t("Select…")}</option>
              {list.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.productName}
                </option>
              ))}
            </select>
          )
        },
        size: 180,
      },
      {
        id: "date",
        header: () => <span>{t("Date")}</span>,
        cell: ({ row }) => {
          const r = row.original.row
          return (
            <DatePicker
              selected={r.collectionDate}
              dateFormat="MMM-yy"
              showMonthYearPicker
              showPopperArrow={false}
              onChange={(date: Date) =>
                updateRow(row.original.key, { collectionDate: date })
              }
              className="form-control"
              placeholderText={t("Pick month")}
            />
          )
        },
        size: 130,
      },
      {
        id: "country",
        header: () => <span>{t("Recycled plastic from")}</span>,
        cell: ({ row }) => {
          const r = row.original.row
          return (
            <select
              value={r.countryId ?? ""}
              onChange={(e) =>
                updateRow(row.original.key, {
                  countryId: e.target.value
                    ? Number(e.target.value)
                    : undefined,
                })
              }
            >
              <option value="">{t("Select…")}</option>
              {countries.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          )
        },
        size: 180,
      },
      {
        id: "volumeNet",
        header: () => <span>{t("Total (kg)")}</span>,
        cell: ({ row }) => {
          const r = row.original.row
          return (
            <input
              type="number"
              min={0}
              step="any"
              placeholder={t("0")}
              value={r.volumeNet ?? ""}
              onChange={(e) =>
                updateRow(row.original.key, {
                  volumeNet: e.target.value
                    ? parseFloat(e.target.value)
                    : undefined,
                })
              }
            />
          )
        },
        size: 120,
      },
      {
        id: "extra",
        header: () => <span>{t("Extra information")}</span>,
        cell: ({ row }) => {
          const r = row.original.row
          return (
            <input
              type="text"
              placeholder={t("Free text")}
              value={r.administrationNumber ?? ""}
              onChange={(e) =>
                updateRow(row.original.key, {
                  administrationNumber: e.target.value,
                })
              }
            />
          )
        },
        size: 180,
      },
    ],
    [
      compositionsByCompany,
      countries,
      ensureCompositions,
      plants,
      polymersOf,
      t,
      updateRow,
    ],
  )

  return (
    <SubmissionGrid<ConvertingInputRow>
      rows={rows}
      columns={columns}
      states={states}
      onAdd={handleAdd}
      onRemove={handleRemove}
      onSubmit={handleSubmit}
      isSubmitting={isSubmitting}
    />
  )
}

export default ConvertingInputGrid
